import * as RadixToast from '@radix-ui/react-toast'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

const variantConfig = {
  success: {
    icon: CheckCircle,
    containerClass: 'border-success-200 bg-white',
    iconClass: 'text-success-500',
    titleClass: 'text-slate-900',
  },
  error: {
    icon: XCircle,
    containerClass: 'border-danger-200 bg-white',
    iconClass: 'text-danger-500',
    titleClass: 'text-slate-900',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'border-warning-200 bg-white',
    iconClass: 'text-warning-500',
    titleClass: 'text-slate-900',
  },
  info: {
    icon: Info,
    containerClass: 'border-primary-200 bg-white',
    iconClass: 'text-primary-500',
    titleClass: 'text-slate-900',
  },
} as const

export function ToastProvider({ children }: { children: React.ReactNode }) {
  return (
    <RadixToast.Provider swipeDirection="right">
      {children}
      <RadixToast.Viewport
        className={cn(
          'fixed bottom-4 right-4 z-50',
          'flex flex-col gap-2',
          'w-80 max-w-[calc(100vw-2rem)]',
          'outline-none'
        )}
      />
    </RadixToast.Provider>
  )
}

export function Toast({
  open,
  onOpenChange,
  title,
  description,
  variant = 'info',
  duration = 4000,
}: ToastProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <RadixToast.Root
      open={open}
      onOpenChange={onOpenChange}
      duration={duration}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-dropdown',
        'data-[state=open]:animate-fade-in',
        'data-[swipe=end]:opacity-0 transition-opacity',
        config.containerClass
      )}
    >
      <Icon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', config.iconClass)} />

      <div className="flex-1 min-w-0">
        <RadixToast.Title className={cn('text-sm font-semibold', config.titleClass)}>
          {title}
        </RadixToast.Title>
        {description && (
          <RadixToast.Description className="text-xs text-slate-500 mt-0.5">
            {description}
          </RadixToast.Description>
        )}
      </div>

      <RadixToast.Close asChild>
        <button
          className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          aria-label="Chiudi notifica"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </RadixToast.Close>
    </RadixToast.Root>
  )
}

// Convenience hook for imperative toast usage
export { RadixToast as ToastPrimitive }
